// The sparkle and the chat it slides in on a desktop (TOM-738, TOM-741):
// wider than 768 px, on every boat page but the chat page and the toolkit
// (lib/neoSlideChat), the sparkle at the bottom right slides Neo's main chat
// (NeoSlideChat) in from the right, over the page. Home, a board, a panel's
// Ask Neo, Neo's headline and every other `nm:open-neo` open the same chat.
// On a board page wider than 1024 px a panel's Ask Neo opens the board's
// floating Edit chat instead, and this chat steps aside (TOM-1049).
//
// It is one panel on every page, the shape home's docked chat has (TOM-920,
// cloud ADR 0451): over the right side of the page, from home's header foot
// (--home-chat-top, which home sets; 144 px elsewhere, what home measures at
// 1440 x 900) to near the window's foot, 440 px wide. The page under it keeps
// its width and stays in reach. The chat's header has Open the full chat and
// Close, and the sparkle steps aside while it is open. Sources in the header,
// or on an answer, widen the panel to the left with Behind the answer at its
// right (slide-chat.css), where the window has room for both.
//
// On home the shell keeps it open or closed per boat, as the owner left it,
// and opens it for Neo's greeting. It is the same chat, still mounted, so
// going to another page and back keeps the thread, the place in it and the
// draft. On home Escape and a link leave it as it is.
//
// Elsewhere it starts closed. Escape closes it, and the focus goes back to the
// sparkle. Following a link to another page closes it too: the page it leads
// to is what the owner asked to see. At 768 px and under home's sparkle opens
// home's chat (BoatHome), and the other pages' sparkle theirs (SparkleChat,
// TOM-547).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ErrorBoundary } from '../ErrorBoundary';
import { NeoSlideChat } from '../NeoChat';
import { Icon } from '../ui/Icon';
import { slideChatButtonLabel, slideChatEscapeCloses } from '../../lib/neoSlideChat';

/** Dialogs and menus take their own Escape, and keep it while they are open. */
const OTHER_ESCAPE = '[role="dialog"], [role="menu"], [aria-modal="true"]';

/** The page states that hide the slide-in (slide-chat.css): it closes for them. */
const HIDING_CLASSES = ['board-editing', 'neo-float-open', 'neo-modal-open', 'nm-kiosk'];

/** A dialog, a menu or a scrim on screen, which owns Escape while it is. */
function otherOverlayOpen(): boolean {
  return (
    Array.from(document.querySelectorAll(OTHER_ESCAPE)).some(
      (el) => el.getClientRects().length > 0
    ) || document.querySelector('dialog[open], .scrim.open') !== null
  );
}

export interface SlideChatDock {
  /** Open on home, as the owner left it (the shell keeps it, per boat). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SlideChat({
  boatId,
  boardPage,
  suggestions,
  fullChatHref,
  dock,
}: {
  boatId: string;
  /** The Neo page, the chat's Open the full chat. */
  fullChatHref: string;
  /** The mode's suggested questions, above the chat's message box (TOM-741). */
  suggestions: readonly string[];
  /**
   * A board page: its board edits go to the Edit chat, and wider than 1024 px
   * its panels' Ask Neo too (TOM-1049).
   */
  boardPage: boolean;
  /** On home: kept open or closed per boat by the shell (TOM-920). */
  dock?: SlideChatDock;
}) {
  // Open for one boat: another boat's chat is closed from its first frame,
  // never drawn open and then slid out (TOM-741).
  const [openFor, setOpenFor] = useState<string | null>(null);
  const docked = dock !== undefined;
  const open = docked ? dock.open : openFor === boatId;
  const dockRef = useRef(dock);
  dockRef.current = dock;
  // Docking or undocking moves the frame at once, with no slide (slide-chat.css).
  const [instant, setInstant] = useState(false);
  const wasDocked = useRef(docked);
  useLayoutEffect(() => {
    if (wasDocked.current === docked) return;
    wasDocked.current = docked;
    setInstant(true);
  }, [docked]);
  useEffect(() => {
    if (!instant) return;
    const frame = requestAnimationFrame(() => requestAnimationFrame(() => setInstant(false)));
    return () => cancelAnimationFrame(frame);
  }, [instant]);
  const [slideIns, setSlideIns] = useState(0);
  const [unread, setUnread] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const closedHere = useRef(false);

  /**
   * Closes it for a reason of the page's (a link, edit mode, the Edit chat):
   * the focus, if it was in the chat, goes back to the sparkle as it does
   * from its header, not to the page's body.
   */
  const closeForPage = useCallback(() => {
    if (!openRef.current) return;
    openRef.current = false;
    closedHere.current = document.activeElement?.closest('#neo-slide-chat') != null;
    setOpenFor(null);
  }, []);

  const openChat = useCallback(
    (next: boolean) => {
      if (next === openRef.current) return;
      openRef.current = next;
      const docking = dockRef.current;
      if (docking !== undefined) {
        docking.onOpenChange(next);
        if (next) setUnread(false);
        else closedHere.current = true;
        return;
      }
      setOpenFor(next ? boatId : null);
      if (next) {
        setSlideIns((n) => n + 1);
        setUnread(false);
      } else {
        closedHere.current = true;
      }
    },
    [boatId]
  );
  const markUnread = useCallback(() => setUnread(true), []);

  // Another boat starts closed, with no dot.
  useEffect(() => {
    setOpenFor(null);
    setUnread(false);
  }, [boatId]);

  // A link followed to another page closes the chat over it.
  const { pathname } = useLocation();
  const seenPath = useRef(pathname);
  useEffect(() => {
    if (pathname === seenPath.current) return;
    seenPath.current = pathname;
    closeForPage();
  }, [pathname, closeForPage]);

  // Closed from its header or Escape, the focus goes back to the sparkle. At
  // once: a window in the background runs no frames.
  useEffect(() => {
    if (open || !closedHere.current) return;
    closedHere.current = false;
    const button = buttonRef.current;
    if (button !== null && button.isConnected && button.getClientRects().length > 0) {
      button.focus({ preventScroll: true });
    }
  }, [open]);

  // Escape closes it from anywhere on the page, once no dialog or menu wants
  // it (the composer's menu, the search, the boat sidebar). Whether one was
  // open is read as the key goes down, before their own listeners close them.
  useEffect(() => {
    if (!open || docked) return;
    let otherOpen = false;
    function onKeyCapture(e: KeyboardEvent) {
      if (e.key === 'Escape') otherOpen = otherOverlayOpen();
    }
    function onKey(e: KeyboardEvent) {
      const target = e.target instanceof Element ? e.target : null;
      const insideOther = otherOpen || target?.closest(OTHER_ESCAPE) != null;
      if (!slideChatEscapeCloses({ key: e.key, defaultPrevented: e.defaultPrevented, insideOther }))
        return;
      e.preventDefault();
      openChat(false);
    }
    window.addEventListener('keydown', onKeyCapture, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKeyCapture, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, docked, openChat]);

  // Edit mode, the Edit chat, a board's maximize or kiosk hide the slide-in
  // (slide-chat.css): it closes for them rather than stay open unseen, so it
  // takes no Escape and misses no new message meanwhile.
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const check = () => {
      if (!HIDING_CLASSES.some((c) => body.classList.contains(c))) return;
      closeForPage();
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [open, closeForPage]);

  return (
    <div
      className={`slide-chat${docked ? ' slide-chat--docked' : ''}${instant ? ' slide-chat--instant' : ''}`}
      data-chat={open ? 'open' : 'closed'}
    >
      {/* A failure in the chat leaves the page standing. */}
      <ErrorBoundary label="slide-in chat">
        <NeoSlideChat
          key={boatId}
          boatId={boatId}
          open={open}
          slideIns={slideIns}
          onOpenChange={openChat}
          onUnread={markUnread}
          boardEditsElsewhere={boardPage}
          suggestions={suggestions}
          panel={{ fullChatHref, onClose: () => openChat(false) }}
        />
      </ErrorBoundary>
      {/* Open, the chat closes from its own header: the sparkle steps aside. */}
      {open ? null : (
        <button
          ref={buttonRef}
          type="button"
          className="tb-icon-btn tb-icon-btn--glass boat-home__sparkle slide-chat__button"
          aria-label={slideChatButtonLabel(false, unread)}
          aria-expanded={false}
          aria-controls="neo-slide-chat"
          title="Neo"
          data-testid="neo-sparkle"
          data-state="closed"
          data-unread={unread ? 'true' : undefined}
          onClick={() => openChat(true)}
        >
          <Icon name="sparkles" size={24} />
          {unread ? <span className="boat-home__unread" aria-hidden="true" /> : null}
        </button>
      )}
    </div>
  );
}
